import React, { useState, useEffect, useCallback } from "react"

import Modal from "components/Modal/MaterialModal"
import InputLabel from "components/Input/LabelInput"

import { Card, CardHeader, CardBody, Button, Input, Badge } from "reactstrap"
import PropTypes from "prop-types"
import { useFormik } from "formik"
import * as Yup from "yup"

import {
  getProvinciasAll,
  saveProvinciaGeo,
  getDepartamentosAll,
  getLocalidadesAll,
  saveLocalidadGeo,
  getCpsPorLocalidad,
  asociarCpALocalidad,
  desasociarCp,
  buscarLocalidadesPorCp,
} from "api/agro"
import toastHandler from "toastHandler"
import userStore from "userStore"

const CardSection = ({ title, count, children }) => (
  <Card className="h-100">
    <CardHeader className="d-flex justify-content-between align-items-center py-2">
      <h6 className="mb-0 fw-bold">{title}</h6>
      {count != null && <Badge color="primary">{count}</Badge>}
    </CardHeader>
    <CardBody
      className="p-0 d-flex flex-column"
      style={{ overflow: "auto", maxHeight: "65vh" }}
    >
      {children}
    </CardBody>
  </Card>
)

const DivisionGeografica = () => {
  const { setSuccess, setError } = toastHandler()
  const user = userStore(state => state.user)

  // Data
  const [provincias, setProvincias] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [localidades, setLocalidades] = useState([])
  const [cps, setCps] = useState([])

  // Selection
  const [provSelected, setProvSelected] = useState(null)
  const [deptoSelected, setDeptoSelected] = useState(null)
  const [locSelected, setLocSelected] = useState(null)

  // Filters
  const [filtroProv, setFiltroProv] = useState("")
  const [filtroDepto, setFiltroDepto] = useState("")
  const [filtroLoc, setFiltroLoc] = useState("")

  // Modals
  const [modal, setModal] = useState(false)
  const [modalType, setModalType] = useState("")
  const [row, setRow] = useState({})
  const [nuevoCp, setNuevoCp] = useState("")
  const [loadingCps, setLoadingCps] = useState(false)

  // Búsqueda combinada (BDSJEA-163): nombre de localidad/departamento o
  // código postal, con provincia opcional como filtro. Si se buscó algo
  // (resultadosBusqueda !== null) se muestra la tabla de resultados en vez
  // de las 4 columnas de navegación.
  const [textoBusqueda, setTextoBusqueda] = useState("")
  const [provinciaBusqueda, setProvinciaBusqueda] = useState("")
  const [resultadosBusqueda, setResultadosBusqueda] = useState(null)
  const [buscando, setBuscando] = useState(false)

  const toggle = () => setModal(!modal)

  // === Load Provincias ===
  useEffect(() => {
    getProvinciasAll().then(setProvincias).catch(setError)
  }, [])

  // === Load Departamentos when province selected ===
  const loadDepartamentos = useCallback(prov => {
    if (!prov) {
      setDepartamentos([])
      return
    }
    getDepartamentosAll(prov).then(setDepartamentos).catch(setError)
  }, [])

  useEffect(() => {
    loadDepartamentos(provSelected)
    setDeptoSelected(null)
    setLocSelected(null)
    setLocalidades([])
    setCps([])
    setFiltroDepto("")
    setFiltroLoc("")
  }, [provSelected])

  // === Load Localidades when department selected ===
  useEffect(() => {
    if (!deptoSelected || !provSelected) {
      setLocalidades([])
      return
    }
    getLocalidadesAll(provSelected, deptoSelected)
      .then(setLocalidades)
      .catch(setError)
    setLocSelected(null)
    setCps([])
    setFiltroLoc("")
  }, [deptoSelected])

  // === Load CPs when locality selected ===
  useEffect(() => {
    if (!locSelected) {
      setCps([])
      return
    }
    setLoadingCps(true)
    getCpsPorLocalidad(locSelected)
      .then(setCps)
      .catch(setError)
      .finally(() => setLoadingCps(false))
  }, [locSelected])

  // === Búsqueda combinada ===
  const esBusquedaPorCp = texto => /^\d+$/.test(texto.trim())

  const handleBuscar = () => {
    const texto = textoBusqueda.trim()
    if (!texto) return

    setBuscando(true)

    if (esBusquedaPorCp(texto)) {
      buscarLocalidadesPorCp(texto)
        .then(filas =>
          setResultadosBusqueda(
            filas.map(f => ({
              provinciaCodigo: f.provincia_codigo,
              provinciaNombre: f.provincia_nombre,
              departamento: f.departamento,
              localidad: f.nombre,
              codigosPostales: f.cp_codigo,
            })),
          ),
        )
        .catch(setError)
        .finally(() => setBuscando(false))
      return
    }

    const provinciaFiltro = provinciaBusqueda || undefined
    Promise.all([
      getLocalidadesAll(provinciaFiltro, undefined, texto),
      getDepartamentosAll(provinciaFiltro, texto),
    ])
      .then(([localidadesEncontradas, departamentosEncontrados]) => {
        const filasLocalidades = localidadesEncontradas.map(l => ({
          provinciaCodigo: l.provincia_codigo,
          provinciaNombre: l.provincia_nombre,
          departamento: l.departamento,
          localidad: l.nombre,
          codigosPostales: l.codigos_postales,
        }))
        const filasDepartamentos = departamentosEncontrados.map(d => ({
          provinciaCodigo: d.provincia_codigo,
          provinciaNombre: d.provincia_nombre,
          departamento: d.departamento,
          localidad: null,
          codigosPostales: null,
        }))
        setResultadosBusqueda([...filasLocalidades, ...filasDepartamentos])
      })
      .catch(setError)
      .finally(() => setBuscando(false))
  }

  const handleLimpiarBusqueda = () => {
    setTextoBusqueda("")
    setProvinciaBusqueda("")
    setResultadosBusqueda(null)
  }

  // === Filtered lists ===
  const provinciasFiltradas = provincias.filter(
    p =>
      !filtroProv ||
      p.nombre.toLowerCase().includes(filtroProv.toLowerCase()) ||
      p.codigo.toLowerCase().includes(filtroProv.toLowerCase()),
  )

  const departamentosFiltrados = departamentos.filter(
    d =>
      !filtroDepto ||
      d.departamento.toLowerCase().includes(filtroDepto.toLowerCase()),
  )

  const localidadesFiltradas = localidades.filter(
    l => !filtroLoc || l.nombre.toLowerCase().includes(filtroLoc.toLowerCase()),
  )

  // === Province CRUD ===
  const provValidation = Yup.object({
    codigo: Yup.string().required("Obligatorio"),
    nombre: Yup.string().required("Obligatorio"),
  })
  const provFormik = useFormik({
    enableReinitialize: true,
    initialValues: { ...row },
    validationSchema: provValidation,
    onSubmit: values => {
      saveProvinciaGeo({ ...values, usuario: user.username })
        .then(() => {
          setSuccess()
          toggle()
          getProvinciasAll().then(setProvincias)
        })
        .catch(setError)
    },
  })

  // === Locality CRUD ===
  const locValidation = Yup.object({
    nombre: Yup.string().required("Obligatorio"),
    departamento: Yup.string().required("Obligatorio"),
    provinciaCodigo: Yup.string().required("Obligatorio"),
  })
  const locFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...row,
      provinciaCodigo: row.provincia_codigo || provSelected || "",
    },
    validationSchema: locValidation,
    onSubmit: values => {
      saveLocalidadGeo({
        id: row.id,
        nombre: values.nombre,
        departamento: values.departamento,
        provinciaCodigo: values.provinciaCodigo,
        latitud: values.latitud || null,
        longitud: values.longitud || null,
      })
        .then(() => {
          setSuccess()
          toggle()
          getLocalidadesAll(provSelected, deptoSelected).then(setLocalidades)
        })
        .catch(setError)
    },
  })

  const formik = modalType === "provincia" ? provFormik : locFormik

  // === CP actions ===
  const handleAsociarCp = () => {
    if (!nuevoCp.trim() || !locSelected) return
    asociarCpALocalidad(locSelected, nuevoCp.trim())
      .then(() => {
        setSuccess()
        setNuevoCp("")
        getCpsPorLocalidad(locSelected).then(setCps)
      })
      .catch(setError)
  }

  const handleDesasociarCp = idCp => {
    desasociarCp(locSelected, idCp)
      .then(() => {
        setSuccess()
        getCpsPorLocalidad(locSelected).then(setCps)
      })
      .catch(setError)
  }

  // === Open modals ===
  const openNew = type => {
    setModalType(type)
    if (type === "provincia") {
      setRow({ codigo: "", nombre: "", region: "" })
    } else {
      setRow({
        nombre: "",
        departamento: deptoSelected || "",
        provincia_codigo: provSelected || "",
        latitud: "",
        longitud: "",
      })
    }
    toggle()
  }

  const colSeleccionable = { cursor: "pointer" }

  return (
    <>
      {/* === BÚSQUEDA COMBINADA === */}
      <Card className="shadow-none border mb-3">
        <CardBody className="d-flex gap-2 flex-wrap align-items-end py-2">
          <div style={{ minWidth: 260, flex: 2 }}>
            <label className="mb-1 d-block small">Buscar</label>
            <Input
              bsSize="sm"
              placeholder="Buscar por nombre (localidad o departamento) o código postal..."
              value={textoBusqueda}
              onChange={e => setTextoBusqueda(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleBuscar()}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <label className="mb-1 d-block small">Provincia</label>
            <Input
              type="select"
              bsSize="sm"
              value={provinciaBusqueda}
              onChange={e => setProvinciaBusqueda(e.target.value)}
              disabled={esBusquedaPorCp(textoBusqueda)}
            >
              <option value="">Todas</option>
              {provincias.map(p => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nombre}
                </option>
              ))}
            </Input>
          </div>
          <Button
            size="sm"
            color="primary"
            onClick={handleBuscar}
            disabled={!textoBusqueda.trim() || buscando}
          >
            Buscar
          </Button>
          {resultadosBusqueda !== null && (
            <Button size="sm" color="light" onClick={handleLimpiarBusqueda}>
              Limpiar búsqueda
            </Button>
          )}
        </CardBody>
      </Card>

      {resultadosBusqueda !== null && (
        <Card className="shadow-none border">
          <CardHeader className="d-flex justify-content-between align-items-center py-2">
            <h6 className="mb-0 fw-bold">Resultados de la búsqueda</h6>
            <Badge color="primary">{resultadosBusqueda.length}</Badge>
          </CardHeader>
          <CardBody className="p-0">
            {resultadosBusqueda.length === 0 ? (
              <p className="text-muted p-3 mb-0">
                No se encontraron resultados para la búsqueda.
              </p>
            ) : (
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Provincia</th>
                    <th>Departamento</th>
                    <th>Localidad</th>
                    <th>CPs</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosBusqueda.map((r, i) => (
                    <tr
                      key={`${r.provinciaCodigo}-${r.departamento}-${r.localidad}-${i}`}
                    >
                      <td>{r.provinciaNombre}</td>
                      <td>{r.departamento}</td>
                      <td>
                        {r.localidad || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        {r.codigosPostales || (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {resultadosBusqueda === null && (
        <div className="row g-3">
          {/* === PROVINCIAS === */}
          <div className="col-md-3">
            <CardSection title="Provincias" count={provincias.length}>
              <div className="p-2">
                <Input
                  bsSize="sm"
                  placeholder="Buscar..."
                  value={filtroProv}
                  onChange={e => setFiltroProv(e.target.value)}
                  className="mb-2"
                />
                <Button
                  size="sm"
                  color="light"
                  className="w-100 mb-2"
                  onClick={() => openNew("provincia")}
                >
                  + Nueva
                </Button>
              </div>
              <div className="list-group list-group-flush">
                {provinciasFiltradas.map(p => (
                  <div
                    key={p.codigo}
                    className={`list-group-item list-group-item-action ${provSelected === p.codigo ? "active" : ""}`}
                    style={colSeleccionable}
                    onClick={() =>
                      setProvSelected(
                        p.codigo === provSelected ? null : p.codigo,
                      )
                    }
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        {p.codigo} - {p.nombre}
                      </span>
                      {provSelected === p.codigo && (
                        <i className="mdi mdi-check-circle" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardSection>
          </div>

          {/* === DEPARTAMENTOS === */}
          <div className="col-md-3">
            <CardSection
              title="Departamentos / Partidos"
              count={departamentos.length}
            >
              {!provSelected ? (
                <p className="text-muted p-3 mb-0">Seleccioná una provincia</p>
              ) : (
                <>
                  <div className="p-2">
                    <Input
                      bsSize="sm"
                      placeholder="Buscar..."
                      value={filtroDepto}
                      onChange={e => setFiltroDepto(e.target.value)}
                      className="mb-2"
                    />
                  </div>
                  <div className="list-group list-group-flush">
                    {departamentosFiltrados.map(d => (
                      <div
                        key={d.departamento}
                        className={`list-group-item list-group-item-action ${deptoSelected === d.departamento ? "active" : ""}`}
                        style={colSeleccionable}
                        onClick={() =>
                          setDeptoSelected(
                            d.departamento === deptoSelected
                              ? null
                              : d.departamento,
                          )
                        }
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{d.departamento}</span>
                          {deptoSelected === d.departamento && (
                            <i className="mdi mdi-check-circle" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardSection>
          </div>

          {/* === LOCALIDADES === */}
          <div className="col-md-3">
            <CardSection title="Localidades" count={localidades.length}>
              {!deptoSelected ? (
                <p className="text-muted p-3 mb-0">
                  Seleccioná un departamento
                </p>
              ) : (
                <>
                  <div className="p-2">
                    <Input
                      bsSize="sm"
                      placeholder="Buscar..."
                      value={filtroLoc}
                      onChange={e => setFiltroLoc(e.target.value)}
                      className="mb-2"
                    />
                    <Button
                      size="sm"
                      color="light"
                      className="w-100 mb-2"
                      onClick={() => openNew("localidad")}
                    >
                      + Nueva
                    </Button>
                  </div>
                  <div className="list-group list-group-flush">
                    {localidadesFiltradas.map(l => (
                      <div
                        key={l.id}
                        className={`list-group-item list-group-item-action ${locSelected === l.id ? "active" : ""}`}
                        style={colSeleccionable}
                        onClick={() =>
                          setLocSelected(l.id === locSelected ? null : l.id)
                        }
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{l.nombre}</span>
                          {locSelected === l.id && (
                            <i className="mdi mdi-check-circle" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardSection>
          </div>

          {/* === CODIGOS POSTALES === */}
          <div className="col-md-3">
            <CardSection title="Códigos Postales" count={cps.length}>
              {!locSelected ? (
                <p className="text-muted p-3 mb-0">Seleccioná una localidad</p>
              ) : loadingCps ? (
                <p className="text-muted p-3 mb-0">Cargando...</p>
              ) : (
                <>
                  <div className="p-2 d-flex gap-1">
                    <Input
                      bsSize="sm"
                      placeholder="Nuevo CP"
                      value={nuevoCp}
                      onChange={e =>
                        setNuevoCp(e.target.value.replace(/[^0-9]/g, ""))
                      }
                      maxLength={4}
                      onKeyDown={e => e.key === "Enter" && handleAsociarCp()}
                    />
                    <Button
                      size="sm"
                      color="primary"
                      onClick={handleAsociarCp}
                      disabled={!nuevoCp.trim()}
                    >
                      +
                    </Button>
                  </div>
                  <div className="list-group list-group-flush">
                    {cps.map(cp => (
                      <div
                        key={cp.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <span>{cp.codigo}</span>
                        <button
                          className="mdi mdi-close-circle text-danger border-0 bg-transparent p-0"
                          onClick={() => handleDesasociarCp(cp.id)}
                          title="Desasociar"
                        />
                      </div>
                    ))}
                    {cps.length === 0 && (
                      <p className="text-muted p-3 mb-0">
                        Sin códigos postales
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardSection>
          </div>
        </div>
      )}

      {/* === MODAL === */}
      <Modal
        modal={modal}
        toggle={toggle}
        modalTitle={
          modalType === "provincia"
            ? row.id
              ? `Editar: ${row.nombre}`
              : "Nueva provincia"
            : row.id
              ? `Editar: ${row.nombre}`
              : "Nueva localidad"
        }
      >
        <div className="mt-4 d-flex gap-3 flex-column">
          {modalType === "provincia" ? (
            <>
              <InputLabel
                label="Código *"
                name="codigo"
                value={provFormik.values.codigo}
                onChange={provFormik.handleChange}
                invalid={!!provFormik.errors.codigo}
                feedback={provFormik.errors.codigo}
              />
              <InputLabel
                label="Nombre *"
                name="nombre"
                value={provFormik.values.nombre}
                onChange={provFormik.handleChange}
                invalid={!!provFormik.errors.nombre}
                feedback={provFormik.errors.nombre}
              />
              <InputLabel
                label="Región"
                name="region"
                value={provFormik.values.region || ""}
                onChange={provFormik.handleChange}
              />
            </>
          ) : (
            <>
              <InputLabel
                label="Nombre *"
                name="nombre"
                value={locFormik.values.nombre}
                onChange={locFormik.handleChange}
                invalid={!!locFormik.errors.nombre}
                feedback={locFormik.errors.nombre}
              />
              <InputLabel
                label="Departamento *"
                name="departamento"
                value={locFormik.values.departamento}
                onChange={locFormik.handleChange}
                invalid={!!locFormik.errors.departamento}
                feedback={locFormik.errors.departamento}
              />
              <InputLabel
                label="Latitud"
                name="latitud"
                value={locFormik.values.latitud || ""}
                onChange={locFormik.handleChange}
              />
              <InputLabel
                label="Longitud"
                name="longitud"
                value={locFormik.values.longitud || ""}
                onChange={locFormik.handleChange}
              />
            </>
          )}
          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button
              color="primary"
              className="shadow-none"
              onClick={formik.handleSubmit}
            >
              Guardar
            </Button>
            <Button color="light" className="shadow-none" onClick={toggle}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

CardSection.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  children: PropTypes.node,
}

export default DivisionGeografica
